import { buildReviewSections, REVIEW_ENTRY_KIND } from "../domain/reviewSummary";
import ReviewNote from "./ReviewNote";

// FINISHED Review body: what was logged, read-only. There is no field, no
// structural action and no dispatch here except an exercise note
// (`onCommitNote`). Changing the workout itself happens on the workout screen,
// reached with the Review's back arrow.

function SetRow({ line, columns, optionalColumns }) {
  return (
    <li
      className="lz-workout-review-set"
      data-performed={line.performed ? "true" : "false"}
    >
      <span className="lz-workout-review-set__index">
        <span className="lz-v2-visually-hidden">Set </span>{line.ordinal}
      </span>
      {line.empty ? (
        <span className="lz-workout-review-set__state lz-workout-review-set__state--only">Not performed</span>
      ) : (
        <>
          {columns.map((column) => (
            <span key={column} className={`lz-workout-review-set__value lz-workout-review-set__value--${column}`}>
              {line.values[column] ||
                (optionalColumns.includes(column) ? null : <span aria-hidden="true">—</span>)}
            </span>
          ))}
          <span className="lz-workout-review-set__tags">
            {line.performed ? null : <span className="lz-workout-review-set__state">Not performed</span>}
          </span>
        </>
      )}
    </li>
  );
}

function ExerciseSummary({ entry, disabled, onCommitNote }) {
  const { item } = entry;
  const headingId = `review-exercise-${item.itemId}`;
  return (
    <article
      className="lz-workout-review-exercise"
      data-item-id={item.itemId}
      data-kind={entry.kind}
      data-skipped={entry.skipped || undefined}
      aria-labelledby={headingId}
    >
      <div className="lz-workout-review-exercise__head">
        {entry.laneLabel ? <span className="lz-workout-lane">{entry.laneLabel}</span> : null}
        <h3 id={headingId}>{entry.name}</h3>
        {entry.skipped ? <span className="lz-workout-review-exercise__status">Skipped</span> : null}
      </div>

      {entry.kind === REVIEW_ENTRY_KIND.PLACEHOLDER ? (
        <p className="lz-workout-review-exercise__muted">{entry.status}</p>
      ) : null}
      {entry.kind === REVIEW_ENTRY_KIND.UNLOGGABLE ? (
        <p className="lz-workout-review-exercise__muted">Nothing could be logged for this exercise.</p>
      ) : null}
      {entry.kind === REVIEW_ENTRY_KIND.CARDIO && !entry.skipped ? (
        <p
          className="lz-workout-review-exercise__cardio"
          data-performed={entry.cardio.completed ? "true" : "false"}
        >
          {entry.cardio.completed ? entry.cardio.value : "Not performed"}
        </p>
      ) : null}
      {entry.kind === REVIEW_ENTRY_KIND.SETS && !entry.skipped && entry.sets.length ? (
        <ol
          className="lz-workout-review-sets"
          aria-label={`${entry.name} sets`}
          style={{ "--lz-workout-review-columns": entry.columns.length }}
        >
          {entry.sets.map((line) => (
            <SetRow key={line.key} line={line} columns={entry.columns} optionalColumns={entry.optionalColumns} />
          ))}
        </ol>
      ) : null}
      {entry.kind === REVIEW_ENTRY_KIND.SETS && !entry.skipped && !entry.sets.length ? (
        <p className="lz-workout-review-exercise__muted">No sets</p>
      ) : null}

      {entry.canNote ? (
        <ReviewNote
          id={`review-note-${item.itemId}`}
          label="Exercise note"
          addLabel="Add note"
          addAriaLabel={`Add note for ${entry.name}`}
          editLabel={`Edit note for ${entry.name}`}
          value={entry.notes}
          maxLength={500}
          disabled={disabled}
          onCommit={(notes) => onCommitNote(item.itemId, notes)}
        />
      ) : null}
    </article>
  );
}

export default function ReviewExerciseList({ record, disabled, onCommitNote }) {
  const sections = buildReviewSections(record);
  if (!sections.length) {
    return <p className="lz-workout-review-empty">No exercises were logged.</p>;
  }
  return (
    <div className="lz-workout-review-exercises">
      {sections.map((section) => (section.group ? (
        <section key={section.key} className="lz-workout-review-group" aria-label={section.group}>
          <p className="lz-workout-review-group__label">{section.group}</p>
          {section.entries.map((entry) => (
            <ExerciseSummary key={entry.key} entry={entry} disabled={disabled} onCommitNote={onCommitNote} />
          ))}
        </section>
      ) : (
        <ExerciseSummary
          key={section.key}
          entry={section.entries[0]}
          disabled={disabled}
          onCommitNote={onCommitNote}
        />
      )))}
    </div>
  );
}
